/**
 * TransactionsSection — the dashboard's Transactions panel, now with its
 * own Company + Period filter (SectionFilter) independent of the
 * dashboard-wide filter at the top of the page. Extracted out of
 * DashboardPage.js so it can own its own filter/search/pagination state.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import SectionFilter from './SectionFilter';
import VoucherTable from './VoucherTable';
import DrillDownModal from './DrillDownModal';
import { useAllVouchers } from '../hooks/useDashboard';
import useDrillDown from '../hooks/useDrillDown';
import { computeRangeForFilter } from '../utils/dateFilters';

const typeTabs = ['All', 'Receipt', 'Payment', 'Sales', 'Purchase'];
const PAGE_SIZE = 12;

const fmtCount = (v) => new Intl.NumberFormat('en-IN').format(Number(v) || 0);

const TransactionsSection = ({ companies }) => {
  const [companyId, setCompanyId] = useState('all');
  const [dateFilter, setDateFilter] = useState('overall');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [typeTab, setTypeTab] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const { from, to } = useMemo(() => {
    if (dateFilter === 'custom') return { from: customFrom, to: customTo };
    return computeRangeForFilter(dateFilter);
  }, [dateFilter, customFrom, customTo]);

  const { data: vouchers, loading, error } = useAllVouchers(companyId, companies, from, to);
  const isAllCompanies = companyId === 'all';

  const drill = useDrillDown();

  const handleOpenVoucher = useCallback((row) => {
    drill.openVoucher({
      companyId: row.companyId,
      guid: row.guid,
      label: row.voucherNumber || row.type || 'Voucher',
    });
  }, [drill]);

  const filteredRows = useMemo(() => {
    let rows = vouchers || [];
    if (typeTab !== 'All') rows = rows.filter((r) => r.type === typeTab);
    const q = (searchTerm || '').trim().toLowerCase();
    if (q) {
      rows = rows.filter((r) =>
        String(r.party || '').toLowerCase().includes(q) ||
        String(r.voucherNumber || '').toLowerCase().includes(q) ||
        String(r.narration || '').toLowerCase().includes(q) ||
        String(r.type || '').toLowerCase().includes(q));
    }
    return rows;
  }, [vouchers, typeTab, searchTerm]);

  const pageCount = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pagedRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [companyId, dateFilter, customFrom, customTo, searchTerm, typeTab]);

  const typeCounts = useMemo(() => {
    const c = { All: (vouchers || []).length };
    (vouchers || []).forEach((v) => { c[v.type] = (c[v.type] || 0) + 1; });
    return c;
  }, [vouchers]);

  const handleReset = () => {
    setCompanyId('all');
    setDateFilter('overall');
    setCustomFrom('');
    setCustomTo('');
    setSearchTerm('');
    setTypeTab('All');
    setPage(1);
  };

  return (
    <section className="panel transactions-panel">
      <div className="panel-header">
        <div>
          <h2>Transactions</h2>
          <p className="panel-subtitle">
            {fmtCount(filteredRows.length)} record(s){isAllCompanies ? ' across all companies' : ''}.
          </p>
        </div>
        <input
          type="search"
          className="txn-search"
          placeholder="Search party, voucher, narration…"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <SectionFilter
        idPrefix="txn"
        companies={companies}
        companyId={companyId}
        onCompanyChange={setCompanyId}
        dateFilter={dateFilter}
        onDateFilterChange={setDateFilter}
        customFrom={customFrom}
        customTo={customTo}
        onCustomFromChange={setCustomFrom}
        onCustomToChange={setCustomTo}
        onReset={handleReset}
      />

      <div className="type-tabs">
        {typeTabs.map((t) => (
          <button
            key={t}
            type="button"
            className={`type-tab ${typeTab === t ? 'type-tab-active' : ''}`}
            onClick={() => setTypeTab(t)}
          >
            {t}
            <span className="type-tab-count">{fmtCount(typeCounts[t] || 0)}</span>
          </button>
        ))}
      </div>

      <VoucherTable
        rows={pagedRows}
        loading={loading}
        error={error}
        showCompany={isAllCompanies}
        onOpenVoucher={handleOpenVoucher}
      />

      {filteredRows.length > 0 && (
        <div className="pagination-controls">
          <button type="button" className="pagination-button" disabled={page <= 1} onClick={() => setPage((p) => Math.max(p - 1, 1))}>
            ← Previous
          </button>
          <span>Page {page} of {pageCount}</span>
          <button type="button" className="pagination-button" disabled={page >= pageCount} onClick={() => setPage((p) => Math.min(p + 1, pageCount))}>
            Next →
          </button>
        </div>
      )}

      {drill.isOpen && (
        <DrillDownModal
          stack={drill.stack}
          onPush={drill.push}
          onPopTo={drill.popTo}
          onClose={drill.close}
        />
      )}
    </section>
  );
};

export default TransactionsSection;
